import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { toast } from 'sonner';
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import PageHeader from "../../../layout/shared/page-header";
import CustomSpinner from '../../../loader/CustomSpinner';
import { setSelectedView } from '../../../redux/headquarterProfileSlice';
import { RootState } from '../../../redux/store';
import { menuRoutes } from "../../../router/menu";
import { HeadquarterService } from '../../../services/headquarters/headquarterService';
import { HeadquarterApiResponse } from '../../../type/entities/headquarter-type';

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.headquarters.profile.info,
        permission: {
            group: 'headquarter',
            action: 'get',
        },
    },
    {
        label: 'Activos (Contenedores/Filtros)',
        path: menuRoutes.headquarters.profile.assets,
        permission: {
            group: 'headquarter',
            action: 'get',
        },
    },
    {
        label: 'Productos',
        path: menuRoutes.headquarters.profile.products,
        permission: {
            group: 'provider_headquarter_recurrent_products',
            action: 'list',
        },
    },
    {
        label: 'Histórico de Recogidas',
        path: menuRoutes.headquarters.profile.pickups,
        permission: {
            group: 'pickup',
            action: 'list',
        },
    }
];

export default function HeadquarterProfileLayout ({ isLoading }: { isLoading?: boolean }) {

    // HOOKS

    const { selectedView } = useSelector((state: RootState) => state.headquarterProfile);
    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const navigate = useNavigate();
    const service = new HeadquarterService();

    // DATA FETCHING

    //--------------------------------------------------------------------------------------------------------------
    /**
     *  @EN FETCH HEADQUARTER DATA BY ID
     *  @ES OBTIENE LOS DATOS DE UNA SEDES POR SU ID
     */
    //--------------------------------------------------------------------------------------------------------------
    const [data, loadingHeadquarter, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getHeadquarterById(id);
        return response.getResponseData() as HeadquarterApiResponse;
    }, [id]));
    //--------------------------------------------------------------------------------------------------------------
    
    const pageHeader = {
        title: `Perfil de ${data?.name || 'Sede'}`,
        breadcrumb: [
            { name: 'Sedes', href: menuRoutes.headquarters.path },
            { name: 'Perfil de Sede' }, 
        ],
    };
    
    // FUNCTIONS

    //--------------------------------------------------------------------------------------------------------------
    /**
     *  @EN HANDLES HEADQUARTER DELETION BY ID
     *  @ES MANEJA LA ELIMINACION DE UNA SEDE POR EL ID
     */
    //--------------------------------------------------------------------------------------------------------------    
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteHeadquarter(id)).getResponseData();
            if (response.success) {
                navigate(menuRoutes.headquarters.path); 
                toast.success('Sede eliminada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        navigate(`${menuRoutes.headquarters.path}/${id}/profile/${selectedView}`);
    }, [selectedView]);

    // RENDER

    return (
        <>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb}>
                <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                </div>
            </PageHeader>

            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {isLoading ? <div className='m-auto h-full'><CustomSpinner /></div> : <Outlet/>}
        </>
    );
}